'use client';

import Badge from '@/components/global/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

type MultiSelectProps = {
  className?: string;
  label?: string;
  placeholder?: string;
  options: {
    label: string;
    value: string;
    color?: string;
  }[];
  maxVisibleBadges?: number;
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  error?: string;
  description?: string;
  defaultValue?: string[];
};

const MultiSelect = ({
  className,
  label,
  placeholder,
  options,
  onChange,
  disabled,
  error,
  description,
  maxVisibleBadges,
  defaultValue,
}: MultiSelectProps) => {
  const [selectedItems, setSelectedItems] = useState<string[]>(
    (defaultValue || []).filter((item) => item !== '')
  );

  useEffect(() => {
    setSelectedItems((defaultValue || []).filter((item) => item !== ''));
  }, [defaultValue]);

  const handleSelectChange = (value: string) => {
    let newSelectedItems: string[];
    if (!selectedItems.includes(value)) {
      newSelectedItems = [...selectedItems, value];
    } else {
      newSelectedItems = selectedItems.filter((item) => item !== value);
    }
    newSelectedItems = newSelectedItems.filter((item) => item !== '');
    setSelectedItems(newSelectedItems);
    onChange?.(newSelectedItems);
  };

  const isOptionSelected = (value: string): boolean => {
    return selectedItems.includes(value);
  };

  const getSelectedLabels = () => {
    return selectedItems.map((value) => {
      const option = options.find((opt) => opt.value === value);
      return option?.label || value;
    });
  };

  const MAX_VISIBLE_BADGES = maxVisibleBadges || 3;

  return (
    <div className='flex flex-col items-start gap-3 self-stretch'>
      {label && (
        <label className='font-satoshi text-base font-medium capitalize text-[#1F2A37] dark:text-white'>
          {label}
        </label>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              'h-[54px] w-full',
              'p-4',
              'rounded-lg bg-white',
              'border-2 border-black/15',
              'hover:bg-black/5',
              'focus:border-[#7846FF] focus:outline-none',
              'ring-0 focus:ring-1 focus:ring-[#7846FF]',
              'dark:border-gray-700 dark:bg-gray-800 dark:text-white',
              'dark:hover:bg-gray-700',
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
              disabled &&
                'cursor-not-allowed opacity-50 hover:bg-white dark:hover:bg-gray-800',
              className
            )}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={
              error ? 'error-message' : description ? 'description' : undefined
            }
          >
            <div className='flex w-full items-center justify-between'>
              {selectedItems.length > 0 ? (
                <div className='flex items-center gap-2 overflow-hidden'>
                  <div className='flex max-h-[32px] flex-wrap gap-2 overflow-y-auto'>
                    {getSelectedLabels()
                      .slice(0, MAX_VISIBLE_BADGES)
                      .map((label) => (
                        <Badge key={label}>{label}</Badge>
                      ))}
                  </div>
                  {selectedItems.length > MAX_VISIBLE_BADGES && (
                    <Badge variant='outline' className='shrink-0'>
                      +{selectedItems.length - MAX_VISIBLE_BADGES}
                    </Badge>
                  )}
                </div>
              ) : (
                <span className='text-[#384250] dark:text-white'>
                  {placeholder || 'Select values'}
                </span>
              )}
              <ChevronDown className='h-4 w-4 opacity-50' />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-56 border-black/15 bg-white dark:border-gray-700 dark:bg-gray-800'
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuLabel className='text-[#1F2A37] dark:text-white'>
            {label}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              onSelect={(e) => e.preventDefault()}
              key={option.value}
              checked={isOptionSelected(option.value)}
              onCheckedChange={() => handleSelectChange(option.value)}
              className='text-[#384250] hover:bg-black/5 dark:text-white dark:hover:bg-gray-700'
            >
              <div className='flex items-center gap-2'>
                {option.color && (
                  <div
                    className='h-3 w-3 rounded-full'
                    style={{ backgroundColor: option.color }}
                  />
                )}
                {option.label}
              </div>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {description && !error && (
        <p
          id='description'
          className='text-sm text-gray-500 dark:text-gray-400'
        >
          {description}
        </p>
      )}
      {error && (
        <p id='error-message' className='text-sm text-red-500'>
          {error}
        </p>
      )}
    </div>
  );
};

export default MultiSelect;
